import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { View, Platform } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import { rem } from 'design-system/values';
import { Button, NavigationHeader, Spacer } from 'design-system';
import StatusBar from 'design-system/StatusBar';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Links from 'utils/links';
import Sentry from 'utils/sentry';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { OnboardingFlow } from 'utils/types/navigation';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { DOB_STORAGE_FORMAT } from 'hooks/useDateTimePickerDOB';
import { format } from 'date-fns';
import { postFunnelEvents } from 'features/funnel-events/actions';
import { InvestFunnelEvents } from 'features/funnel-events/types';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import * as constants from '../constants';
import { useCommonInvestStyles } from './styles';
import { useOnboardingState } from '../hooks/selectors';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
import { selectNetworth, selectAnnualIncome, selectFundingSource, selectEmploymentStatus, selectShowMultiAccountTradingView, selectHideFundingAccountAndAutoinvestFromInvestOnboarding, } from '../reducers/selectors';
import { clearOnboardingState } from '../actions';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { useCreateInvestAccount } from '../hooks/useCreateInvestAccount';
export const strings = {
    next: 'Continue',
    brokerAgreement: 'Broker agreement',
    orderHandlingPolicy: 'Order handling and\xa0best\xa0execution\xa0policy ',
    riskWarning: 'Risk disclosure',
    tradingTerms: 'Trading terms',
    open: 'Open',
    pdfScreenButtonTitle: 'Accept and go back',
    custodianTerms: 'Custodian terms',
    custodianTermsId: 'custodianTermsId',
    orderExecutionPolicy: 'Order execution policy',
    orderExecutionPolicyId: 'orderExecutionPolicyId',
    ISATerms: 'S&S ISA terms',
    JISATerms: 'Junior ISA terms',
    ISADocumentTitle: 'ISA terms',
    JISADocumentTitle: 'Junior ISA terms',
    ISATermsId: 'ISATermsId',
    JISATermsId: 'JISATermsId',
    riskWarningId: 'riskWarningId',
    tradingTermsId: 'tradingTermsId',
    brokerAgreementId: 'brokerAgreementId',
    orderHandlingPolicyId: 'orderHandlingPolicyId',
};
const BrokerRequirementsScreen = ({ navigation, route: { params } }) => {
    const mountedOnAnotherScreen = params?.mountedOnAnotherScreen;
    const scrollY = useSharedValue(0);
    const commonStyles = useCommonInvestStyles(mountedOnAnotherScreen);
    const styles = useStyles(styleSet);
    const colors = useColors();
    const isMigrationToSecclFlow = useAppSelector(selectShowMultiAccountTradingView);
    const onboardingState = useOnboardingState();
    const accountType = onboardingState?.accountType;
    const networth = useAppSelector(selectNetworth);
    const annualIncomes = useAppSelector(selectAnnualIncome);
    const fundingSource = useAppSelector(selectFundingSource);
    const employmentStatus = useAppSelector(selectEmploymentStatus);
    const navigateToNextScreen = useNavigateToNextScreen();
    const hideFundingAccountAndAutoinvestFlow = useAppSelector(selectHideFundingAccountAndAutoinvestFromInvestOnboarding);
    const dispatch = useAppDispatch();
    const { submit } = useCreateInvestAccount();
    useEffect(() => {
        if (!accountType) {
            dispatch(clearOnboardingState());
            navigation.popToTop();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useAmplitudeOnMountEvent(OnboardingFlow.Invest, !!mountedOnAnotherScreen, 'Invest T&Cs Screen', {
        type: accountType ? accountType.toLowerCase() : 'gia',
    });
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const [state, setState] = useState({
        [strings.riskWarning]: false,
        [strings.tradingTerms]: false,
        [strings.brokerAgreement]: false,
        [strings.orderHandlingPolicy]: false,
        [strings.custodianTerms]: false,
        [strings.orderExecutionPolicy]: false,
        [strings.ISATerms]: false,
    });
    const onContinue = useCallback(() => {
        if (accountType === 'GIA' &&
            (!state[strings.riskWarning] ||
                !state[strings.tradingTerms] ||
                !state[strings.custodianTerms] ||
                !state[strings.orderExecutionPolicy])) {
            return;
        }
        if (accountType === 'ISA' &&
            (!state[strings.ISATerms] ||
                !state[strings.riskWarning] ||
                !state[strings.tradingTerms] ||
                !state[strings.custodianTerms] ||
                !state[strings.orderExecutionPolicy])) {
            return;
        }
        if (accountType === 'JISA' &&
            (!state[strings.JISATerms] ||
                !state[strings.riskWarning] ||
                !state[strings.tradingTerms] ||
                !state[strings.custodianTerms] ||
                !state[strings.orderExecutionPolicy])) {
            return;
        }
        const { isAffiliatedExchangeOrFinra, isControlPerson, isPoliticallyExposed } = onboardingState?.params || {};
        if (!isMigrationToSecclFlow && !(networth && annualIncomes && fundingSource && employmentStatus)) {
            const dataObj = {
                annualIncomes: !!annualIncomes,
                networth: !!networth,
                fundingSource: !!fundingSource,
                employmentStatus: !!employmentStatus,
                isControlPerson: typeof isControlPerson === 'boolean',
                isPoliticallyExposed: typeof isPoliticallyExposed === 'boolean',
                isAffiliatedExchangeOrFinra: typeof isAffiliatedExchangeOrFinra === 'boolean',
            };
            const message = (() => {
                if (!annualIncomes) {
                    return 'No annual income info.';
                }
                if (!networth) {
                    return 'No networth info.';
                }
                if (!fundingSource) {
                    return 'No funding source info.';
                }
                if (!employmentStatus) {
                    return 'No employment status info.';
                }
                return 'This is awkward, please try again.';
            })();
            Modal.showError(message);
            Sentry.logError({
                filename: 'BrokerRequirementsScreen',
                message: 'User not able to go ahead',
                data: dataObj,
            });
            navigation.popToTop();
            return;
        }
        if (accountType === 'JISA') {
            if (!onboardingState?.lastName ||
                !onboardingState?.firstName ||
                !onboardingState?.dob ||
                !onboardingState?.childAddress ||
                typeof onboardingState.hasJISAOrTrustFund !== 'boolean') {
                navigation.popToTop();
                dispatch(clearOnboardingState());
                return;
            }
            dispatch(postFunnelEvents([InvestFunnelEvents.ONB_INVEST_NEXT_ISA_DECLARATION]));
            navigation.navigate('InvestSecclISADeclarationScreen', {
                jisaChildDetails: {
                    surname: onboardingState.lastName,
                    firstName: onboardingState.firstName,
                    dateOfBirth: format(onboardingState.dob, DOB_STORAGE_FORMAT),
                    hasPriorJisa: onboardingState.hasJISAOrTrustFund,
                    address: onboardingState.childAddress,
                },
            });
        }
        else if (accountType === 'ISA') {
            dispatch(postFunnelEvents([InvestFunnelEvents.ONB_INVEST_NEXT_ISA_DECLARATION]));
            navigation.navigate('InvestSecclISADeclarationScreen');
        }
        else {
            if (hideFundingAccountAndAutoinvestFlow) {
                submit();
                return;
            }
            dispatch({ type: 'SAVE_ONBOARDING_STATE', didSignDeclaration: true, params: {} });
            navigateToNextScreen();
        }
    }, [
        accountType,
        annualIncomes,
        employmentStatus,
        fundingSource,
        hideFundingAccountAndAutoinvestFlow,
        isMigrationToSecclFlow,
        navigateToNextScreen,
        networth,
        onboardingState?.childAddress,
        onboardingState?.dob,
        onboardingState?.firstName,
        onboardingState?.hasJISAOrTrustFund,
        onboardingState?.lastName,
        onboardingState?.params,
        state,
        submit,
    ]);
    const onOpenLink = useCallback((link) => () => {
        if (Platform.OS === 'android') {
            navigation.navigate('Pdf', { url: link, buttonTitle: strings.pdfScreenButtonTitle });
        }
        else {
            Links.openLink(link);
        }
    }, []);
    const handlePress = useCallback((title) => () => {
        setState((oldState) => ({
            ...oldState,
            [title]: !oldState[title],
        }));
    }, []);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y > 0 ? contentOffset.y : 0;
    });
    const renderRow = useCallback((title, documentTitle, link, testId) => (<>
        <TouchableBounce onPress={handlePress(title)} testID={testId}>
          <View style={styles.itemContainer}>
            <RadioButton type="multiple" isChecked={!!state[title]}/>
            <Spacer.W16 />
            <View style={styles.flex}>
              <Text Text-16 flex>
                {title}
              </Text>
              <Text TextThin-14 Secondary>
                I have read & agree to the{' '}
                <Text TextThin-14 Primary onPress={onOpenLink(link)} suppressHighlighting>
                  {documentTitle}
                </Text>
              </Text>
            </View>
          </View>
        </TouchableBounce>
        <Spacer.H16 />
      </>), [handlePress, onOpenLink, state, styles.flex, styles.itemContainer]);
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const isDisabled = useMemo(() => {
        if (accountType === 'GIA') {
            return (!state[strings.riskWarning] ||
                !state[strings.tradingTerms] ||
                !state[strings.custodianTerms] ||
                !state[strings.orderExecutionPolicy]);
        }
        if (accountType === 'JISA') {
            return (!state[strings.JISATerms] ||
                !state[strings.riskWarning] ||
                !state[strings.tradingTerms] ||
                !state[strings.custodianTerms] ||
                !state[strings.orderExecutionPolicy]);
        }
        return (!state[strings.ISATerms] ||
            !state[strings.riskWarning] ||
            !state[strings.tradingTerms] ||
            !state[strings.custodianTerms] ||
            !state[strings.orderExecutionPolicy]);
    }, [accountType, state]);
    const onPressAll = useCallback(() => {
        const allDocuments = (() => {
            if (accountType === 'GIA') {
                return {
                    [strings.riskWarning]: true,
                    [strings.tradingTerms]: true,
                    [strings.custodianTerms]: true,
                    [strings.orderExecutionPolicy]: true,
                };
            }
            if (accountType === 'JISA') {
                return {
                    [strings.JISATerms]: true,
                    [strings.riskWarning]: true,
                    [strings.tradingTerms]: true,
                    [strings.custodianTerms]: true,
                    [strings.orderExecutionPolicy]: true,
                };
            }
            return {
                [strings.ISATerms]: true,
                [strings.riskWarning]: true,
                [strings.tradingTerms]: true,
                [strings.custodianTerms]: true,
                [strings.orderExecutionPolicy]: true,
            };
        })();
        setState((oldState) => ({
            ...oldState,
            ...allDocuments,
        }));
    }, [accountType]);
    if (!accountType) {
        return null;
    }
    return (<View style={commonStyles.containerNoPadding}>
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader scrollY={scrollY} regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={OnboardingFlow.Invest}/> : undefined} rightContent={<View style={!!mountedOnAnotherScreen && styles.rightBtnContainer}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView scrollEnabled scrollEventThrottle={16} onScroll={scrollHandler} keyboardShouldPersistTaps="handled" contentContainerStyle={commonStyles.contentContainer}>
        <ScreenInfoHeader title={onboardingTitleStrings.Terms.title} subTitle={onboardingTitleStrings.Terms.subtitle}/>
        <TouchableBounce onPress={onPressAll}>
          <View style={styles.itemContainer}>
            <RadioButton type="multiple" isChecked={!isDisabled}/>
            <Spacer.W16 />
            <View style={styles.flex}>
              <Text TextThin-14 flex>
                I agree with all {accountType === 'ISA' || accountType === 'JISA' ? 5 : 4} documents below
              </Text>
            </View>
          </View>
        </TouchableBounce>
        <Spacer.H16 />
        {renderRow(strings.tradingTerms, strings.tradingTerms, constants.SECCL_TRADING_TERMS, strings.tradingTermsId)}
        {renderRow(strings.custodianTerms, strings.custodianTerms, constants.SECCL_CUSTODIAN_TERMS, strings.custodianTermsId)}
        {renderRow(strings.orderExecutionPolicy, strings.orderExecutionPolicy, constants.SECCL_ORDER_EXECUTION_POLICY, strings.orderExecutionPolicyId)}
        {renderRow(strings.riskWarning, strings.riskWarning, constants.SECCL_RISK_DISCLOSURE, strings.riskWarningId)}
        {accountType === 'ISA' &&
            renderRow(strings.ISATerms, strings.ISADocumentTitle, constants.SECCL_ISA_TERMS, strings.ISATermsId)}
        {accountType === 'JISA' &&
            renderRow(strings.JISATerms, strings.JISADocumentTitle, constants.SECCL_JISA_TERMS, strings.JISATermsId)}
      </Animated.ScrollView>
      <Button brand onPress={onContinue} testID="ContinueBtn" title={strings.next} disabled={isDisabled} style={[mountedOnAnotherScreen ? commonStyles.mountedButton : commonStyles.button]}/>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    itemContainer: {
        flexDirection: 'row',
        padding: rem(16),
        marginHorizontal: rem(16),
        alignItems: 'center',
        borderRadius: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    rightBtnContainer: {
        paddingRight: rem(16),
        marginBottom: rem(6),
        alignItems: 'flex-end',
    },
}));
export default BrokerRequirementsScreen;
